'use client';

import { useState, useEffect } from 'react';
import { useEvent, useInterval } from 'react-use';

import useScript from '@/hooks/useScript';
import { useBreakpoint } from '@/hooks';
import getEnvironmentVariables from '@/server/utils/getEnvironmentVars';
import { trpc } from '@/trpc';
import usePartyNumber from '@/hooks/usePartyNumber';

import { IsLiveChatScheduledOptions, isLiveChatScheduled } from './util/isLiveChatScheduled';

export type LiveChatWorkHours = Pick<IsLiveChatScheduledOptions, 'hourOpen' | 'hourClosed'>;

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    embedded_svc: any;
  }
}

interface ChatWidgetInnerProps {
  isMobile: boolean;
  partyNumber: string;
  displayName: string;
  workhours: LiveChatWorkHours;
  envVars: Record<string, string>;
}

/**
 * ChatWidgetInner is the component that renders the Salesforce Live Agent chat widget.
 * It uses the Embedded Service Chat feature in Salesforce.
 * This component is responsible for initializing the chat widget and handling the chat lifecycle.
 * It also contains the custom styles for the chat widget to match our branding.
 * The chat widget is only shown when agents are available and the user is within
 * the chat hours. The `useInterval` keeps track of when to show/hide the chat widget.
 *
 * Official Salesforce Documentation for Embedded Service Chat:
 * https://developer.salesforce.com/docs/atlas.en-us.snapins_web_dev.meta/snapins_web_dev/snapins_web_overview.htm
 *
 * @param isMobile - Whether the chat widget is being rendered on a mobile device
 * @param partyNumber - The user's party number
 * @param displayName - The user's display name used in the chat conversations
 * @param workhours - The hours the chat is open (in 24-hour format)
 * @param envVars - The environment variables needed to initialize the chat widget
 */
const ChatWidgetInner = ({ isMobile, partyNumber, displayName, workhours, envVars }: ChatWidgetInnerProps) => {
  const [isLiveChatAvailable, setIsLiveChatAvailable] = useState(
    isLiveChatScheduled(new Date(), {
      daysClosed: ['Sat', 'Sun'],
      hourOpen: workhours.hourOpen,
      hourClosed: workhours.hourClosed
    })
  );
  const [isActive, setIsActive] = useState<boolean>(false);

  const {
    NEXT_PUBLIC_LIVE_CHAT_BASE_URL,
    NEXT_PUBLIC_LIVE_CHAT_BUTTON_ID,
    NEXT_PUBLIC_LIVE_CHAT_COMM_CASE_RECORD_TYPE,
    NEXT_PUBLIC_LIVE_CHAT_CONTENT_BASE_URL,
    NEXT_PUBLIC_LIVE_CHAT_DEPLOYMENT_ID,
    NEXT_PUBLIC_LIVE_CHAT_FORCE_URL,
    NEXT_PUBLIC_LIVE_CHAT_ORGANIZATION_ID,
    NEXT_PUBLIC_LIVE_CHAT_SCRIPT_SRC_BASE,
    NEXT_PUBLIC_LIVE_CHAT_SHOWN_WHEN_DISABLED
  } = envVars;

  const isChatHiddenWhenDisabled = NEXT_PUBLIC_LIVE_CHAT_SHOWN_WHEN_DISABLED !== 'true';
  const status = useScript(`${NEXT_PUBLIC_LIVE_CHAT_SCRIPT_SRC_BASE}/embeddedservice/5.0/esw.min.js`);

  useEvent('onbeforeunload', () => {
    window.embedded_svc.liveAgentAPI.endChat();
  });

  useEffect(() => {
    if (status !== 'ready') {
      return;
    }

    const initESW = (gslbBaseURL: string | null, partyId: string, firstName: string) => {
      window.embedded_svc.settings.displayHelpButton = true;
      window.embedded_svc.settings.language = ''; // For example, enter 'en' or 'en-US'

      window.embedded_svc.settings.enabledFeatures = ['LiveAgent'];
      window.embedded_svc.settings.entryFeature = 'LiveAgent';
      window.embedded_svc.settings.defaultMinimizedText = 'Chat with a Live Agent';

      // Configures the top-level domain for retaining state across subdomains
      // https://developer.salesforce.com/docs/atlas.en-us.snapins_web_dev.meta/snapins_web_dev/snapins_web_set_domain.htm
      window.embedded_svc.settings.storageDomain = 'omers.com';

      // for autoloading post chat
      // https://developer.salesforce.com/docs/atlas.en-us.snapins_web_dev.meta/snapins_web_dev/snapins_web_postchat.htm
      window.embedded_svc.settings.autoOpenPostChat = true;

      // https://developer.salesforce.com/docs/atlas.en-us.snapins_web_dev.meta/snapins_web_dev/snapins_web_prechat_details.htm
      window.embedded_svc.settings.extraPrechatFormDetails = [
        {
          label: 'Party ID',
          value: partyId,
          displayToAgent: true
        },
        {
          label: 'First Name',
          name: 'FirstName',
          value: firstName,
          displayToAgent: true
        },
        {
          label: 'Status',
          value: 'New',
          displayToAgent: true
        },
        {
          label: 'Communication Case Record Type',
          value: NEXT_PUBLIC_LIVE_CHAT_COMM_CASE_RECORD_TYPE,
          displayToAgent: true
        }
      ];

      // https://developer.salesforce.com/docs/atlas.en-us.snapins_web_dev.meta/snapins_web_dev/snapins_web_prechat_details.htm
      window.embedded_svc.settings.extraPrechatInfo = [
        {
          entityName: 'Contact',
          showOnCreate: true,
          linkToEntityName: 'Case',
          linkToEntityField: 'ContactId',
          saveToTranscript: 'ContactId',
          entityFieldMaps: [
            {
              isExactMatch: true,
              fieldName: 'OM_Party_Id__c',
              doCreate: false,
              doFind: true,
              label: 'Party ID'
            }
          ]
        },
        {
          entityName: 'Case',
          showOnCreate: false,
          saveToTranscript: 'CaseId',
          entityFieldMaps: [
            {
              isExactMatch: false,
              fieldName: 'Status',
              doCreate: true,
              doFind: false,
              label: 'Status'
            },
            {
              doCreate: true,
              doFind: false,
              fieldName: 'RecordTypeId',
              isExactMatch: false,
              label: 'Communication Case Record Type'
            }
          ]
        }
      ];

      window.embedded_svc.init(
        NEXT_PUBLIC_LIVE_CHAT_SCRIPT_SRC_BASE,
        NEXT_PUBLIC_LIVE_CHAT_FORCE_URL,
        gslbBaseURL,
        NEXT_PUBLIC_LIVE_CHAT_ORGANIZATION_ID,
        'OM_Live_Chat',
        {
          baseLiveAgentContentURL: NEXT_PUBLIC_LIVE_CHAT_CONTENT_BASE_URL,
          deploymentId: NEXT_PUBLIC_LIVE_CHAT_DEPLOYMENT_ID,
          buttonId: NEXT_PUBLIC_LIVE_CHAT_BUTTON_ID,
          baseLiveAgentURL: NEXT_PUBLIC_LIVE_CHAT_BASE_URL,
          eswLiveAgentDevName: 'OM_Live_Chat',
          isOfflineSupportEnabled: false
        }
      );
    };

    initESW(null, partyNumber, displayName);
    window.embedded_svc.addEventHandler('afterMaximize', () => setIsActive(true));

    window.embedded_svc.addEventHandler('afterDestroy', () => setIsActive(false));

    // eslint-disable-next-line consistent-return
    return () => {
      window.embedded_svc.liveAgentAPI?.endChat();
    };
  }, [
    status,
    partyNumber,
    displayName,
    NEXT_PUBLIC_LIVE_CHAT_COMM_CASE_RECORD_TYPE,
    NEXT_PUBLIC_LIVE_CHAT_SCRIPT_SRC_BASE,
    NEXT_PUBLIC_LIVE_CHAT_FORCE_URL,
    NEXT_PUBLIC_LIVE_CHAT_ORGANIZATION_ID,
    NEXT_PUBLIC_LIVE_CHAT_CONTENT_BASE_URL,
    NEXT_PUBLIC_LIVE_CHAT_DEPLOYMENT_ID,
    NEXT_PUBLIC_LIVE_CHAT_BUTTON_ID,
    NEXT_PUBLIC_LIVE_CHAT_BASE_URL
  ]);

  const liveChatAvailabilityCheckInterval = 1000 * 60 * 1;

  // Every ${liveChatAvailabilityCheckInterval} minutes, check if the live chat is available
  // based on the current time and the days/hours of operation
  useInterval(() => {
    setIsLiveChatAvailable(
      isLiveChatScheduled(new Date(), {
        daysClosed: ['Sat', 'Sun'],
        hourOpen: workhours.hourOpen,
        hourClosed: workhours.hourClosed
      })
    );
  }, liveChatAvailabilityCheckInterval);

  return (
    <>
      <style
        type="text/css"
        id="liveChatCustomStyles"
        /* eslint-disable-next-line react/no-danger */
        dangerouslySetInnerHTML={{
          __html: `
        .embeddedServiceHelpButton > * {  
          font-family: var(--font-roboto-flex), sans-serif;
        }

        .embeddedServiceHelpButton .helpButton .uiButton {
          box-shadow: none;
          background-color: ${isMobile ? 'transparent' : '#0078B5'}; 
          ${isMobile ? 'margin: 0; border-radius: 32px; z-index: 150;' : ''}
          ${!isLiveChatAvailable ? 'display: none !important;' : ''}
        }
        
        #helpButtonSpan {
          font-family: var(--font-roboto-flex), sans-serif;
          font-size: 16px;
        }

        .embeddedServiceSidebarDialogState #dialogTextTitle {
          font-size: 16px;
          font-family: var(--font-roboto-flex), sans-serif;
          font-weight: bold;  
        }

        .embeddedServiceSidebarDialogState #dialogTextBody {
          font-size: 1rem;
          font-family: var(--font-roboto-flex), sans-serif;
        }

        .sidebarHeader h2,
        .dialogButton {
          font-size: 16px;
          font-weight: 700;
        }

        .embeddedServiceLiveAgentStateChatPlaintextMessageDefaultUI.plaintextContent {
          font-size: 1rem;
        }

        .embeddedServiceSidebar.layout-docked .dockableContainer {
          box-shadow: none;
        }

        .startButton,
        .dialog-button-0{
          height: 48px;
          border-radius: 5px;
          background-color: #D36816 !important;
          font-family: var(--font-roboto-flex), sans-serif;
          font-weight: bold;
          font-size: 19px;
        }
        .startButton:hover,
        .dialog-button-0:hover{
          background-color: #B15006 !important;
        }
        
        .endChatButton{
          height: 48px;
          border-radius: 5px;
          background-color: #D36816 !important;
          font-family: var(--font-roboto-flex), sans-serif;
          font-weight: bold;
          font-size: 19px;
          text-decoration: none !important;
        }
        .endChatButton:hover{
          background-color: #B15006 !important;
          text-decoration: none !important;
        }
        
        .waitingCancelChat,
        .dialog-button-1 {
          height: 48px;
          border-radius: 5px;
          border: 1.5px solid #D36816;
          font-family: var(--font-roboto-flex), sans-serif;
          font-weight: bold;
          font-size: 19px;
          text-decoration: none !important;
        }
        
        .waitingCancelChat span,
        .dialog-button-1 span {
          color: #D36816 !important;
          text-decoration: none !important;
        }
        
        .waitingCancelChat:hover,
        .dialog-button-1:hover{
          border: 1.5px solid #B15006;
          background-color: #FFFBF4 !important;
        }
        
        .waitingCancelChat span:hover
        .dialog-button-1 span:hover{
          color: #B15006 !important;
        }

        h2[embeddedService-chatHeader_chatHeader] {
          font-weight: bold;    
         }

        .embeddedServiceHelpButton .helpButton .uiButton:focus {
          outline: 1px solid #003A70;
        }
        @font-face {
          font-family: 'Salesforce Sans';
          src: url('https://c1.sfdcstatic.com/etc/clientlibs/sfdc-aem-master/clientlibs_base/fonts/SalesforceSans-Regular.woff') format('woff'),
          url('https://c1.sfdcstatic.com/etc/clientlibs/sfdc-aem-master/clientlibs_base/fonts/SalesforceSans-Regular.ttf') format('truetype');
        }

        .embeddedServiceIcon {
          background-image: url(/images/liveChat/icon-livechat.svg);
          height: 64px;
          width: 64px;
          top: ${isMobile ? '0' : '-10px'};
          margin: 0 !important;
          display: ${!isActive ? 'inline-block !important' : 'none'}
        }

        .embeddedServiceHelpButton .helpButton {
          bottom: ${isMobile ? '40px' : '0'} !important;
        }

        .embeddedServiceHelpButton .embeddedServiceIcon::before {
          content: none;
        }

        .embeddedServiceHelpButton .helpButton .helpButtonEnabled:focus::before, 
        .embeddedServiceHelpButton .helpButton .helpButtonEnabled:hover {
          ${isMobile ? 'border-radius: 32px' : ''}
        }

        .helpButtonLabel {
          padding-right: 15px;
        }

        .uiButton {
          ${isMobile && !isActive ? 'width: 64px !important; min-width: 64px !important; min-height: 64px;' : ''}
          ${!isMobile ? 'min-width: 275px !important;' : ''}
          ${isMobile && 'padding: 0 !important;'}
        }

        button.dialogButton.dialog-button-0.uiButton.embeddedServiceSidebarButton {
          background-color: #D36816 !important;
          font-family: var(--font-roboto-flex), sans-serif;
          font-weight: bold;
          font-size: 19px;
          text-decoration: none;
        }

        button.dialogButton.dialog-button-1.uiButton--inverse.uiButton.embeddedServiceSidebarButton {
          border-radius: 5px;
          border: 1.5px solid #D36816;
          font-size: 19px;
          font-family: var(--font-roboto-flex), sans-serif;
          font-weight: bold;
        }

        .helpButtonEnabled:focus .helpButtonLabel,
        .embeddedServiceSidebarButton:focus {
          text-decoration: none !important;
        }

        .embeddedServiceLiveAgentStateChatInputFooter .chatActionButton.Send {
          ${isMobile && isActive ? 'display: none;' : ''}
        }

        .embeddedServiceSidebarMinimizedDefaultUI.sidebarHeader {
          ${
            isMobile
              ? `
            background-image: url(/images/liveChat/icon-livechat.svg);
            bottom: 20px;
            height: 64px;
            width: 64px;
            padding: 0 !important;
            border-radius: 32px;
            background-color: transparent;
            border: none;
            box-shadow: none;`
              : ''
          }
        }
        .embeddedServiceSidebarMinimizedDefaultUI.sidebarHeader .content {
          ${isMobile ? 'display: none' : ''};
        }

        #helpButtonSpan {
          ${isMobile ? 'display: none' : ''};
        }

        #fileUploadForm,
        #fileUploadIframe,
        #esw_storage_iframe,
        #auraErrorMessage,
        .embeddedServiceHelpButton,
        .embeddedServiceSidebar {
          pointer-events: auto;
          opacity: 1;
        }
      `
        }}
      />
      <div data-testid="chat-widget-inner" />

      {/* Hide the chat when agents are not available */}
      {isChatHiddenWhenDisabled && (
        <style
          type="text/css"
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={{
            __html: `
          .embeddedServiceHelpButton .helpButton button.uiButton.helpButtonDisabled {
            display: none !important;
          }
        `
          }}
        />
      )}
    </>
  );
};

type ChatWidgetProps = {
  liveChatOpenHour: number;
  liveChatOpenMinute: number;
  liveChatCloseHour: number;
  liveChatCloseMinute: number;
};

/**
 * ChatWidget is the container component that:
 * - Fetches the user's party number, display name
 * - receives the chat hours from contentful via the parent component
 * - decides whether to render the ChatWidgetInner component or not
 */
const ChatWidget = ({
  liveChatOpenHour,
  liveChatOpenMinute,
  liveChatCloseHour,
  liveChatCloseMinute
}: ChatWidgetProps) => {
  const { isMobileOrTablet } = useBreakpoint();
  const partyNumber = usePartyNumber().unwrapOr(null);

  const { data: personalInfo, error: personalInfoError } = trpc.profileAndContactInfo.getPersonalInformation.useQuery(
    undefined,
    {
      enabled: !!partyNumber,
      retry: false
    }
  );

  if (personalInfoError) {
    console.info('Error fetching personal information:', personalInfoError);

    return null;
  }

  const displayName = personalInfo?.preferredName ?? personalInfo?.name?.firstName;

  const envVars = getEnvironmentVariables([
    'NEXT_PUBLIC_LIVE_CHAT_BASE_URL',
    'NEXT_PUBLIC_LIVE_CHAT_BUTTON_ID',
    'NEXT_PUBLIC_LIVE_CHAT_COMM_CASE_RECORD_TYPE',
    'NEXT_PUBLIC_LIVE_CHAT_CONTENT_BASE_URL',
    'NEXT_PUBLIC_LIVE_CHAT_DEPLOYMENT_ID',
    'NEXT_PUBLIC_LIVE_CHAT_FORCE_URL',
    'NEXT_PUBLIC_LIVE_CHAT_ORGANIZATION_ID',
    'NEXT_PUBLIC_LIVE_CHAT_SCRIPT_SRC_BASE',
    'NEXT_PUBLIC_LIVE_CHAT_SHOWN_WHEN_DISABLED'
  ]);

  if (envVars.isErr()) {
    console.info('Error fetching environment variables:', envVars.error);

    return null;
  }

  const workhoursOpen = liveChatOpenHour + liveChatOpenMinute / 60;
  const workhoursClose = liveChatCloseHour + liveChatCloseMinute / 60;

  return partyNumber && displayName ? (
    <ChatWidgetInner
      isMobile={!!isMobileOrTablet}
      partyNumber={partyNumber}
      displayName={displayName}
      workhours={{ hourOpen: workhoursOpen, hourClosed: workhoursClose }}
      envVars={envVars.value}
    />
  ) : null;
};

export default ChatWidget;
